import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as yup from "yup";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox } from "@material-ui/core";
import { Button, Form } from "react-bootstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import { normalizeProductKey, toFloat } from "../utils/shopGridDiscounts";

// ─────────────────────────────────────────────────────────────
// Helpers esterni (riutilizzabili e testabili)
// ─────────────────────────────────────────────────────────────
const parseDiscountFromLabel = (label) => {
  const match = String(label).match(/([+-]?\s*\d+(?:[.,]\d+)?)\s*%/);
  if (!match) return null;
  const pct = toFloat(match[1], NaN);
  return Number.isFinite(pct) && pct > 0 ? Math.min(pct / 100, 1) : 0;
};

const hasPercentageInLabel = (label) => /([+-]?\s*\d+(?:[.,]\d+)?)\s*%/.test(String(label));

// ─────────────────────────────────────────────────────────────
// Schema di validazione
// ─────────────────────────────────────────────────────────────
const validationSchemaBuilder = (richiediDatiVendita) => {
  return yup.object({
    idCliente: richiediDatiVendita
      ? yup.number("Inserire un cliente valido").moreThan(0, "Selezionare un cliente").required("Seleziona un cliente")
      : undefined,
    metodoPagamento: richiediDatiVendita
      ? yup
          .number("Inserire un metodo di pagamento valido")
          .moreThan(0, "Selezionare un metodo di pagamento")
          .required("Seleziona un metodo di pagamento")
      : undefined,
    note: yup.string("Inserire una descrizione valida").min(3, "Minimo tre caratteri"),
  });
};

const formFields = {
  idCliente: {
    label: "Cliente",
    placeholder: "Selezionare un cliente...",
    newLabel: "Aggiungi nuovo",
    newLink: "/clienti/#/new",
  },
  metodoPagamento: {
    label: "Metodo di pagamento",
    placeholder: "Selezionare un metodo...",
  },
  note: {
    label: "Note",
    placeholder: "Note..",
    rows: 3,
  },
  extra: {
    label: "Richiesta EXTRA (mese corrente)",
    description: "Prosegui senza spuntare la casella per una Richiesta REGOLARE (mese successivo)",
  },
};

// ─────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────
export const ShopFinalizeDialog = React.memo(function ShopFinalizeDialog({
  open,
  onClose,
  onCloseAndReset,
  result,
  richiestaLoading,
  richiediDatiVendita,
  richiediExtra,
  clientiOptions,
  metodoPagamentiOptions,
  initialIdCliente,
  initialMetodoPagamento,
  initialNote,
  onSubmit,
  cart,
  elementsMap,
  getScontoFinalValue,
  redirectTo,
  discountValidationMessage,
  serverErrorMessage,
  richiestaExtra,
  onRichiestaExtraChange,
}) {
  const [metodoPagamentoSelected, setMetodoPagamentoSelected] = useState(null);

  const formik = useFormik({
    initialValues: {
      idCliente: initialIdCliente,
      metodoPagamento: initialMetodoPagamento,
      note: initialNote || "",
    },
    enableReinitialize: true,
    validationSchema: () => validationSchemaBuilder(richiediDatiVendita),
    onSubmit: (values) => onSubmit(values),
  });

  const { values, errors, setFieldError, setFieldTouched } = formik;

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) return "is-invalid";
    if (formik.touched[fieldname] && !formik.errors[fieldname]) return "is-valid";
    return "";
  };

  // ─────────────────────────────────────────────────────────
  // Label corrente del metodo di pagamento (priorità: selected > options)
  // ─────────────────────────────────────────────────────────
  const paymentLabel = useMemo(() => {
    return String(
      metodoPagamentoSelected ??
      metodoPagamentiOptions?.[formik.values.metodoPagamento] ??
      ""
    );
  }, [formik.values.metodoPagamento, metodoPagamentoSelected, metodoPagamentiOptions]);

  // ✅ hasMetodoPagamentoDiscount: TRUE SOLO SE LA LABEL CONTIENE UNA PERCENTUALE (es. "+5%")
  const hasMetodoPagamentoDiscount = hasPercentageInLabel(paymentLabel);

  // Calcolo sconto effettivo: da label OPPURE fallback da config
  const metodoPagamentoDiscount = useMemo(() => {
    return parseDiscountFromLabel(paymentLabel) ??
           toFloat(metodoPagamentiOptions?.discounts?.[formik.values.metodoPagamento], 0);
  }, [paymentLabel, formik.values.metodoPagamento, metodoPagamentiOptions]);

  // Totale prodotti con sconti applicati
  const totalDiscountedByProduct = useMemo(() => {
    return Object.keys(cart).reduce((sum, id) => {
      const element = elementsMap.get(normalizeProductKey(id)) || elementsMap.get(id);
      if (!element || typeof element.cost === "undefined") return sum;
      return sum + (1 - getScontoFinalValue(id) / 100) * toFloat(element.cost, 0) * toFloat(cart[id], 0);
    }, 0);
  }, [cart, elementsMap, getScontoFinalValue]);

  // Totale finale con sconto metodo di pagamento
  const totalWithMetodoPagamentoDiscount = useMemo(() => {
    return ((1 - metodoPagamentoDiscount) * totalDiscountedByProduct).toFixed(2);
  }, [metodoPagamentoDiscount, totalDiscountedByProduct]);

  // ─────────────────────────────────────────────────────────
  // Validazione note per metodo "Altro"
  // ─────────────────────────────────────────────────────────
  const isMetodoPagamentoAltro = metodoPagamentoSelected?.toLowerCase().includes("altro");
  const noteTrimmed = (values.note || "").toString().trim();
  const isNoteValid = noteTrimmed.length >= 3;
  const disableConfirm = isMetodoPagamentoAltro && !isNoteValid;

  useEffect(() => {
    if (!isMetodoPagamentoAltro || isNoteValid) {
      if (errors.note) setFieldError("note", undefined);
      return;
    }
    setFieldTouched("note", true, false);
    setFieldError("note", "Inserisci il metodo di pagamento nelle note");
  }, [isMetodoPagamentoAltro, isNoteValid, errors.note, setFieldError, setFieldTouched]);

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="richiesta-dialog-title"
      aria-describedby="richiesta-dialog-description"
    >
      <DialogTitle id="richiesta-dialog-title">
        {result === ""
          ? richiestaLoading
            ? "Richiesta in corso"
            : "Finalizza richiesta"
          : parseInt(result, 10) === 203
          ? "Richiesta inviata"
          : "Errore"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="richiesta-dialog-description" component="div">
          {result === "" ? (
            richiestaLoading ? null : (
              <>
                {discountValidationMessage ? (
                  <div className="alert alert-warning" role="alert" style={{ whiteSpace: "pre-wrap" }}>
                    {discountValidationMessage}
                  </div>
                ) : null}
                <Form className="row">
                  {richiediDatiVendita ? (
                    <>
                      <Form.Group className="col-12">
                        <Form.Label className="col-12">
                          {formFields.idCliente.label}
                          <Link to={formFields.idCliente.newLink} style={{ float: "right" }}>
                            {formFields.idCliente.newLabel}
                          </Link>
                        </Form.Label>
                        <Select
                          name="idCliente"
                          classNamePrefix="react-select"
                          value={clientiOptions.find((m) => m.value === formik.values.idCliente) || null}
                          onChange={(selected) => {
                            formik.setFieldValue("idCliente", selected?.value || 0);
                          }}
                          options={clientiOptions}
                          formatOptionLabel={(e) => (
                            <div>
                              <div><strong>{e.label}</strong></div>
                              <div style={{ fontSize: "0.90em" }}>
                                {e.address}, {e.cap} {e.comune} ({e.provincia})
                              </div>
                              <div style={{ fontSize: "0.85em" }}>Rif. {e.referente}</div>
                            </div>
                          )}
                          styles={{
                            singleValue: (provided) => ({
                              ...provided,
                              margin: 0,
                              padding: 0,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              fontSize: "1rem",
                            }),
                          }}
                          components={{
                            SingleValue: ({ children }) => (
                              <div style={{ padding: "8px 12px" }} className="react-select__single-value">
                                {children}
                              </div>
                            ),
                          }}
                        />
                        {formik.touched.idCliente && formik.errors.idCliente ? (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">{formik.errors.idCliente}</div>
                          </div>
                        ) : null}
                      </Form.Group>

                      <Form.Group className="col-12">
                        <Form.Label className="col-12">{formFields.metodoPagamento.label}</Form.Label>
                        <select
                          type="text"
                          name="metodoPagamento"
                          className={`form-control ${getInputClasses("metodoPagamento")}`}
                          onChange={(e) => {
                            formik.handleChange(e);
                            setMetodoPagamentoSelected(e.target.options?.[e.target.selectedIndex]?.text || "");
                          }}
                          value={formik.values.metodoPagamento}
                        >
                          <option value={0}>{formFields.metodoPagamento.placeholder}</option>
                          {Object.entries(metodoPagamentiOptions).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        {formik.touched.metodoPagamento && formik.errors.metodoPagamento ? (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">{formik.errors.metodoPagamento}</div>
                          </div>
                        ) : null}

                        {hasMetodoPagamentoDiscount && (
                          <>
                            <br />
                            Valore finale con ulteriore sconto: {totalWithMetodoPagamentoDiscount}€
                          </>
                        )}
                      </Form.Group>

                      <Form.Group className="col-12">
                        <Form.Label className="col-12">{formFields.note.label}</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="note"
                          placeholder={formFields.note.placeholder}
                          rows={formFields.note.rows}
                          style={{ resize: "none" }}
                          className={getInputClasses("note")}
                          {...formik.getFieldProps("note")}
                        />
                        {formik.touched.note && formik.errors.note ? (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">{formik.errors.note}</div>
                          </div>
                        ) : null}
                      </Form.Group>
                    </>
                  ) : null}

                  {richiediExtra ? (
                    <Form.Group className="col-12">
                      <Form.Label className="col-11">{formFields.extra.label}</Form.Label>
                      <Checkbox
                        name="extra"
                        className="form-control col-1"
                        checked={Boolean(richiestaExtra)}
                        onChange={(_, checked) => onRichiestaExtraChange(checked)}
                      />
                      <div className="col-12 text-muted">{formFields.extra.description}</div>
                    </Form.Group>
                  ) : null}
                </Form>
              </>
            )
          ) : parseInt(result, 10) === 203 ? (
            <>
              La tua richiesta è stata effettuata correttamente, puoi consultare le tue richieste nella tua sezione di{" "}
              <Link to={redirectTo}>Riepilogo</Link>
            </>
          ) : (
            serverErrorMessage || "C'è stato un errore nel processare la richiesta"
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {result === "" ? (
          !richiestaLoading ? (
            <Button onClick={formik.handleSubmit} color="primary" autoFocus disabled={disableConfirm}>
              Conferma
            </Button>
          ) : null
        ) : (
          <Button onClick={onCloseAndReset} color="primary">
            Chiudi
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

// ─────────────────────────────────────────────────────────
// PropTypes
// ─────────────────────────────────────────────────────────
ShopFinalizeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseAndReset: PropTypes.func.isRequired,
  result: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  richiestaLoading: PropTypes.bool.isRequired,
  richiediDatiVendita: PropTypes.bool.isRequired,
  richiediExtra: PropTypes.bool.isRequired,
  clientiOptions: PropTypes.array.isRequired,
  metodoPagamentiOptions: PropTypes.object.isRequired,
  initialIdCliente: PropTypes.number.isRequired,
  initialMetodoPagamento: PropTypes.number.isRequired,
  initialNote: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  elementsMap: PropTypes.instanceOf(Map).isRequired,
  getScontoFinalValue: PropTypes.func.isRequired,
  redirectTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]).isRequired,
  discountValidationMessage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  serverErrorMessage: PropTypes.string.isRequired,
  richiestaExtra: PropTypes.bool.isRequired,
  onRichiestaExtraChange: PropTypes.func.isRequired,
};
